import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from "vue";
  import { BuilderInterfaceWithHouseDesigns } from "@/types/BuilderInterface";
  import router from "@/router";
  import store from "@/store";
  import BuilderEstimatorToolHero from "@/components/builder/estimator-tool/BuilderEstimatorToolHero.vue";
  import HousesDesignsGrid from "./estimator-tool/HousesDesignsGrid.vue";
  import BuilderEstimatorSelectionHouse from "@/components/builder/estimator-tool/builder-estimator-selection-house/BuilderEstimatorSelectionHouse.vue";
  import BuilderEstimatorFloorplans from "@/components/builder/estimator-tool/BuilderEstimatorFloorplans.vue";
  import BuilderEstimatorSelectionHouseStyle from "@/components/builder/estimator-tool/BuilderEstimatorSelectionHouseStyle.vue";
  import BuilderEstimatorHouseUpgradeOption from "@/components/builder/estimator-tool/BuilderEstimatorHouseUpgradeOption.vue";
  import { SELECTION_HOUSE_STYLE_INSTRUCTIONS } from "@/constants/selectionHouseStyleInstructions";
  import BuilderStepNavigationContainer from "@/components/builder/stepper/BuilderStepNavigationContainer.vue";
  import BuilderEstimatorInclusionsPack from "@/components/builder/estimator-tool/BuilderEstimatorInclusionsPack.vue";
  import BuilderEstimatorQuoterSummary from "@/components/builder/estimator-tool/BuilderEstimatorQuoterSummary.vue";
  import BuilderEstimatorSummary from "@/components/builder/estimator-tool/BuilderEstimatorSummary.vue";
  import { GetHousesByQuery } from "@/types/GetHousesByQuery";
  import { BuilderEstimatorSteps } from "@/enums/BuilderEstimatorSteps";
  import { useToast } from "vue-toastification";
  import { useBuilderEstimatorStepValidations } from "@/hooks/estimator-tool/useBuilderEstimatorStepValidations";
  import { useBuilderEstimatorStepActions } from "@/hooks/estimator-tool/useBuilderEstimatorStepActions";
  import { HouseDesignInterface } from "@/types/HouseDesignInterface";
  import { AxiosResponse } from "axios";
  import { StyleOption } from "@/types/BuilderEstimatorStyleOption";
  import { BUILDER_ESTIMATOR_ALL_STEPS } from "@/constants/BuilderEstimatorAllSteps";
  import {
    FormDataSteps,
    useBuilderEstimatorSelections,
  } from "@/hooks/estimator-tool/useBuilderEstimatorSelections";
  import { setBuilderBrandColor } from "@/helpers/builder/setBuilderBrandColor";
  import BuilderEstimatorMobileHeader from "@/components/builder/estimator-tool/BuilderEstimatorMobileHeader.vue";
  import { calculateBuilderEstimatorQuoteSummaryTotalPrice } from "@/helpers/calculateBuilderEstimatorQuoteSummaryTotalPrice";
  import BuilderEstimatorHouseFeaturesMobile from "@/components/builder/estimator-tool/builder-estimator-selection-house/house-features/BuilderEstimatorHouseFeaturesMobile.vue";
  import BuilderEstimatorHouseHeaderMobile from "@/components/builder/estimator-tool/builder-estimator-selection-house/BuilderEstimatorHouseHeaderMobile.vue";
  import { useIsDesktop } from "@/features/useIsDesktop";
  import { filterInactiveHouses } from "@/helpers/builder/filterInactiveHouses";
  import { FacadeInterface } from "@/types/FacadeInterface";
  import { sortByPriceAscending } from "@/helpers/sortByPriceAscending";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'BuilderEstimatorTool',
  setup(__props, { expose: __expose }) {
  __expose();

  const { isDesktop } = useIsDesktop(980);
  const toast = useToast();
  const currentStep = ref<BuilderEstimatorSteps>(
    BuilderEstimatorSteps.LAND_DETAILS
  );
  const errorOnCurrentStep = ref<boolean>(false);
  const houseId = computed(() => {
    return router.currentRoute.value.query.houseId ?? null;
  });

  const summaryModalIsOpen = ref<boolean>(false);
  const houses = ref<HouseDesignInterface[]>([]);
  const houseDesign = computed(
    () => formData.value[BuilderEstimatorSteps.SELECT_DESIGN].houseDesign
  );
  const builder = ref<BuilderInterfaceWithHouseDesigns | null>(null);
  const builderId = computed(
    () => (router.currentRoute.value.params.builderId as string) ?? null
  );

  const loading = ref<boolean>(false);
  const getHouses = (query?: GetHousesByQuery) => {
    loading.value = true;
    store
      .dispatch("builder/getHousesByQuery", {
        ...query,
        builderId: builderId.value,
      })
      .then((res: AxiosResponse<HouseDesignInterface[]>) => {
        if (res.data.length === 0) {
          houses.value = [];
          onNotHousesFound();
          return;
        }

        houses.value = filterInactiveHouses(res.data);
        skipHouseDesignStepIfHouseIdExistsOnURL();
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const getBuilder = () => {
    loading.value = true;
    store
      .dispatch("builder/getBuilderWithHouses", builderId.value)
      .then((res: AxiosResponse<BuilderInterfaceWithHouseDesigns[]>) => {
        const builderResponse = res.data[0];

        if (!builderResponse.houses.length) {
          router.push({ name: "Dashboard" });
          return;
        }

        builder.value = builderResponse;
        setBuilderBrandColor(builder.value.color);
        skipLandDetailsStepIfHouseIdExistsOnURL();
      })
      .finally(() => {
        loading.value = false;
      });
  };
  const mapFacadeImages = (facades: FacadeInterface[]) => {
    return facades.map((facade) => ({
      ...facade,
      imagesUrls: [facade.url],
    }));
  };

  const onSelectNext = () => {
    const currentValidation = stepValidations.value[currentStep.value];

    if (currentValidation && !currentValidation.validate()) {
      errorOnCurrentStep.value = true;
      toast.error("Please, select an option before proceeding");
      return;
    }

    errorOnCurrentStep.value = false;

    const currentIndex = availableBuilderSteps.value.indexOf(currentStep.value);

    if (currentIndex < availableBuilderSteps.value.length - 1) {
      currentStep.value = availableBuilderSteps.value[currentIndex + 1];
    }

    nextStepAction(currentStep.value);
    window.scrollTo(0, 0);
  };

  const onSelectPrev = () => {
    const currentIndex = availableBuilderSteps.value.indexOf(currentStep.value);

    if (currentStep.value === BuilderEstimatorSteps.SELECT_DESIGN) {
      onSelectHouseDesign({
        houseDesignId: null,
      });
    }

    if (currentIndex > 0) {
      currentStep.value = availableBuilderSteps.value[currentIndex - 1];
    }

    window.scrollTo(0, 0);
  };
  const onNotHousesFound = () => {
    onSelectPrev();
    toast.error(
      "No houses found for the selected options, please try again with different land dimensions"
    );
  };

  const {
    formData,
    onSelectOptionStyle,
    onSelectLandDetails,
    onSelectInclusionPack,
    onSelectHouseDesign,
    onViewDesignSelect,
    onSelectOptionalUpgrade,
  } = useBuilderEstimatorSelections(builderId.value, houses, onSelectNext);

  const { stepValidations } = useBuilderEstimatorStepValidations(
    formData.value
  );

  const { nextStepAction } = useBuilderEstimatorStepActions(
    formData.value,
    builderId.value,
    getHouses
  );

  const availableBuilderSteps = computed(() => {
    const stepsToFilterByDesignFeatures = [
      {
        condition: !houseDesign.value?.internalColorSchemes?.length,
        step: BuilderEstimatorSteps.SELECT_INTERNAL_COLOURS,
      },
      {
        condition:
          !formData.value[BuilderEstimatorSteps.SELECT_FACADE]?.facade
            ?.externalColorSchemes?.length,
        step: BuilderEstimatorSteps.SELECT_EXTERNAL_COLOURS,
      },
      {
        condition: false,
        step: BuilderEstimatorSteps.SELECT_FACADE,
      },
      {
        condition: !houseDesign.value?.inclusionsPack?.length,
        step: BuilderEstimatorSteps.SELECT_INCLUSIONS,
      },
      {
        condition: !houseDesign.value?.optionalUpgrades?.length,
        step: BuilderEstimatorSteps.OPTIONAL_UPGRADES,
      },
    ];

    if (!houseDesign.value) {
      return BUILDER_ESTIMATOR_ALL_STEPS;
    }

    const steps = [...BUILDER_ESTIMATOR_ALL_STEPS];

    return steps.filter(
      (step) =>
        !stepsToFilterByDesignFeatures.some(
          (filter) => filter.condition && filter.step === step
        )
    );
  });

  const handleOptionStyle = (option: StyleOption) => {
    onSelectOptionStyle(option, currentStep.value as FormDataSteps);
  };

  const toggleSummaryModal = () => {
    summaryModalIsOpen.value = !summaryModalIsOpen.value;
  };

  const updateSummaryModalState = (newState: boolean) => {
    summaryModalIsOpen.value = newState;
  };

  const calculateTotalPrice = computed(() => {
    return calculateBuilderEstimatorQuoteSummaryTotalPrice(formData.value);
  });

  const skipLandDetailsStepIfHouseIdExistsOnURL = () => {
    if (!houseId.value) return;

    onSelectLandDetails({
      width: "",
      length: "",
    });
    onSelectNext();
  };

  const skipHouseDesignStepIfHouseIdExistsOnURL = () => {
    if (!houseId.value) return;

    const existingHouseDesign = houses.value.find(
      (house) => house._id === houseId.value
    );

    if (!existingHouseDesign) {
      currentStep.value = BuilderEstimatorSteps.VIEW_DESIGN;
      return;
    }

    onSelectHouseDesign({
      houseDesignId: existingHouseDesign._id,
    });
    onSelectNext();
    currentStep.value = BuilderEstimatorSteps.SELECT_DESIGN;
  };

  watch(houses, (newHouses) => {
    if (currentStep.value !== BuilderEstimatorSteps.VIEW_DESIGN) return;

    if (!newHouses.length) onNotHousesFound();
  });
  onMounted(() => {
    getBuilder();
  });

const __returned__ = { isDesktop, toast, currentStep, errorOnCurrentStep, houseId, summaryModalIsOpen, houses, houseDesign, builder, builderId, loading, getHouses, getBuilder, mapFacadeImages, onSelectNext, onSelectPrev, onNotHousesFound, formData, onSelectOptionStyle, onSelectLandDetails, onSelectInclusionPack, onSelectHouseDesign, onViewDesignSelect, onSelectOptionalUpgrade, stepValidations, nextStepAction, availableBuilderSteps, handleOptionStyle, toggleSummaryModal, updateSummaryModalState, calculateTotalPrice, skipLandDetailsStepIfHouseIdExistsOnURL, skipHouseDesignStepIfHouseIdExistsOnURL, computed, onMounted, ref, watch, get BuilderInterfaceWithHouseDesigns() { return BuilderInterfaceWithHouseDesigns }, get router() { return router }, get store() { return store }, BuilderEstimatorToolHero, HousesDesignsGrid, BuilderEstimatorSelectionHouse, BuilderEstimatorFloorplans, BuilderEstimatorSelectionHouseStyle, BuilderEstimatorHouseUpgradeOption, get SELECTION_HOUSE_STYLE_INSTRUCTIONS() { return SELECTION_HOUSE_STYLE_INSTRUCTIONS }, BuilderStepNavigationContainer, BuilderEstimatorInclusionsPack, BuilderEstimatorQuoterSummary, BuilderEstimatorSummary, get GetHousesByQuery() { return GetHousesByQuery }, get BuilderEstimatorSteps() { return BuilderEstimatorSteps }, get useToast() { return useToast }, get useBuilderEstimatorStepValidations() { return useBuilderEstimatorStepValidations }, get useBuilderEstimatorStepActions() { return useBuilderEstimatorStepActions }, get HouseDesignInterface() { return HouseDesignInterface }, get AxiosResponse() { return AxiosResponse }, get StyleOption() { return StyleOption }, get BUILDER_ESTIMATOR_ALL_STEPS() { return BUILDER_ESTIMATOR_ALL_STEPS }, get FormDataSteps() { return FormDataSteps }, get useBuilderEstimatorSelections() { return useBuilderEstimatorSelections }, get setBuilderBrandColor() { return setBuilderBrandColor }, BuilderEstimatorMobileHeader, get calculateBuilderEstimatorQuoteSummaryTotalPrice() { return calculateBuilderEstimatorQuoteSummaryTotalPrice }, BuilderEstimatorHouseFeaturesMobile, BuilderEstimatorHouseHeaderMobile, get useIsDesktop() { return useIsDesktop }, get filterInactiveHouses() { return filterInactiveHouses }, get FacadeInterface() { return FacadeInterface }, get sortByPriceAscending() { return sortByPriceAscending } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})